/**
 * The handful of built-ins this app and its libraries call that older iPads
 * and Android WebViews do not have.
 *
 * ## Why, and why only these
 *
 * The build targets ES2022 syntax, which every engine this app supports
 * parses. *Library* methods are a different matter: a call to a method that
 * does not exist is not a syntax error the build can lower, it is a
 * `TypeError` at the moment it runs, and it is found by a pilot.
 *
 * Measured against the built bundle (2026-10-14), not guessed:
 *
 *  - **`Array.prototype.toSorted`** — Safari 16, Chrome 110. Vuetify's
 *    `VHighlight` calls it to mark the matched letters in every `v-select`,
 *    `v-autocomplete` and `v-combobox` list, so on iPadOS 15 — the last
 *    system an iPad Air 2 or an iPad mini 4 can run — typing into the
 *    aerodrome picker threw instead of offering an aerodrome.
 *  - **`crypto.randomUUID`** — Safari 15.4, Chrome 92. Every record the app
 *    creates takes its id from it (25 call sites), so on iPadOS 15.0–15.3
 *    nothing could be created at all.
 *  - **`Array.prototype.at` / `findLast` / `findLastIndex`**, **`Object.hasOwn`**
 *    — Safari 15.4. Vuetify's ripple calls `findLast` on every tap, Pinia's
 *    `$patch` calls `Object.hasOwn`, and the app uses `.at(-1)`.
 *
 * Each is installed only where it is missing and is written to the spec's
 * observable behaviour for the way it is used here — not a general-purpose
 * shim. `structuredClone` and `AbortSignal.timeout` are not here because the
 * code that uses them already falls back (`core/clone.ts`,
 * `services/api/http.ts`).
 *
 * Imported first in `main.ts`, so it runs before any library module does.
 */

type Indexable<T> = { readonly length: number; readonly [index: number]: T }

function define(target: object, name: string, value: unknown): void {
	if (name in target) return
	Object.defineProperty(target, name, { value, writable: true, configurable: true, enumerable: false })
}

function relativeIndex(length: number, index: number): number {
	const integer = Math.trunc(index) || 0
	return integer < 0 ? length + integer : integer
}

function at<T>(this: Indexable<T>, index: number): T | undefined {
	const position = relativeIndex(this.length, index)
	return position < 0 || position >= this.length ? undefined : this[position]
}

define(Array.prototype, 'at', at)
define(String.prototype, 'at', at)
for (const typed of [
	Int8Array,
	Uint8Array,
	Uint8ClampedArray,
	Int16Array,
	Uint16Array,
	Int32Array,
	Uint32Array,
	Float32Array,
	Float64Array,
]) {
	define(typed.prototype, 'at', at)
}

define(Array.prototype, 'findLastIndex', function findLastIndex<
	T,
>(this: T[], predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: unknown) {
	for (let index = this.length - 1; index >= 0; index -= 1) {
		if (predicate.call(thisArg, this[index]!, index, this)) return index
	}
	return -1
})

define(Array.prototype, 'findLast', function findLast<
	T,
>(this: T[], predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: unknown) {
	for (let index = this.length - 1; index >= 0; index -= 1) {
		if (predicate.call(thisArg, this[index]!, index, this)) return this[index]
	}
	return undefined
})

define(Array.prototype, 'toSorted', function toSorted<T>(this: T[], compare?: (a: T, b: T) => number) {
	return Array.prototype.slice.call(this).sort(compare)
})

define(Array.prototype, 'toReversed', function toReversed<T>(this: T[]) {
	return Array.prototype.slice.call(this).reverse()
})

define(Object, 'hasOwn', (object: object, key: PropertyKey) =>
	Object.prototype.hasOwnProperty.call(object, key),
)

/**
 * RFC 4122 version 4, from the same CSPRNG `randomUUID` would use. Only where
 * `getRandomValues` exists — which is everywhere this app runs — so an id is
 * never drawn from `Math.random`.
 */
if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
	define(crypto, 'randomUUID', function randomUUID(): string {
		const bytes = crypto.getRandomValues(new Uint8Array(16))
		bytes[6] = (bytes[6]! & 0x0f) | 0x40
		bytes[8] = (bytes[8]! & 0x3f) | 0x80
		const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
		return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
	})
}

export {}
